import { api } from '../../../general/apiScenarios';
import { CurrentUser } from '../../../general/model';
import { BLOptions, getConceptTypeId } from '../../../general/modelCreativeFolder';
import { getPlotRecognition } from '../../../general/modelPlotRecognition';
import { CHNode } from '../../../general/node';
import { Interrupt, InterruptReason } from '../../../general/scenarioFactory';
import { _select, _take, ListenerApiLight } from '../../listenerMiddleware';
import { logger } from '../../logger';
import { getUser } from '../app/selectors';
import { CreativeBLSettings } from '../creativeWorkflow/legacy_models/model';

import {
  detectBLContext,
  getNodes,
  loadBLContext,
} from '../creativeWorkflow/legacy_sagas/pureSagas';
import {
  buildConvertNodeFallbacks,
  collectConvertSourceFiles,
  normalizeConvertRootNode,
} from '../creativeWorkflow/convertSourceAnalysis';
import * as fileManagerActions from '../fileManager/actions';
import * as creativeConverterActions from './actions';
import { CreativeConverterJob } from './model';
import { getCache } from './selectors';
import { convertWorkflow } from '../creativeWorkflow/workflows/convertWorkflow';
import type { NodeFallback } from '../creativeWorkflow/legacy_models/fallback';

export async function convertFolder(
  lapi: ListenerApiLight,
  creativeConverterJob: CreativeConverterJob,
) {
  const error = createErrorHandler(lapi, creativeConverterJob.node.id);
  /**
   * Step 1: Gathering information
   */
  // get bl context
  let settings: CreativeBLSettings;
  try {
    settings = await detectBLContext(lapi, creativeConverterJob.node.id).all();
  } catch (x) {
    return error(
      (x as Interrupt).message === InterruptReason.USER_CANCEL
        ? 'Conversion was cancelled'
        : (x as Interrupt).message ?? 'Some contents did not pass validation',
    );
  }
  lapi.signal.throwIfAborted();

  /**
   * Step 2: validating and fixing
   */

  let nodesFallbacks: NodeFallback[];

  // normalize folder contents and prepare metadata fallbacks
  try {
    nodesFallbacks = await prepareNodesFallbacks(lapi, creativeConverterJob.node, settings);
  } catch (x) {
    return error(
      (x as Interrupt).message === InterruptReason.USER_CANCEL
        ? 'Conversion was cancelled'
        : (x as Interrupt).message ?? 'Some contents did not pass validation',
    );
  }
  lapi.signal.throwIfAborted();
  /**
   * Step 3: finalizing
   */

  // загружаем данные OAuth юзера
  const _user = _select(lapi, getUser);
  if (!_user) {
    return error('Load user failed');
  }

  let convertResult;
  try {
    convertResult = await convertWorkflow(
      {
        nodesFallbacks,
        settings,
        skipManualValidation: creativeConverterJob.skipManualProcessing,
      },
      lapi.signal,
    );
  } catch (x) {
    return error((x as Error).message ?? 'Failed to prepare creative files');
  }
  lapi.signal.throwIfAborted();

  if (!convertResult) {
    return error('Conversion was cancelled');
  }

  // Convert the folder into a creative folder only after the user confirmed
  // the final metadata and rename actions in the editor.
  const createCreativeFolderResult = await api.creative.creativeAttach(
    creativeConverterJob.node.id,
    createBLOptions(settings, _user),
    [],
  );
  lapi.signal.throwIfAborted();
  if (!createCreativeFolderResult.success) {
    return error('Failed to create creative');
  }

  const creativeContext = await loadBLContext(lapi, creativeConverterJob.node.id);
  lapi.signal.throwIfAborted();
  if (!creativeContext.success) {
    return error('Failed to load creative context');
  }

  if (!Array.isArray(convertResult.preparedFiles)) {
    return error('Failed to prepare creative files: invalid format');
  }

  const invalidFiles = convertResult.preparedFiles
    .map((file, idx) => ({ file, idx }))
    .filter(({ file }) => !file || typeof file !== 'object' || !file.meta);

  if (invalidFiles.length > 0) {
    const details = invalidFiles
      .map(({ file, idx }) => `file[${idx}]: ${file ? 'missing.meta' : 'null'}`)
      .join(', ');
    return error(`Filed to prepare creative metadata: ${details}`);
  }

  for (const file of convertResult.preparedFiles) {
    const nodeId = file.nodeRef?.id;
    if (!nodeId) {
      return error('Failed to bind creative files');
    }

    const bindResult = await api.bl.blCreateCreativeFile(
      nodeId,
      creativeContext.data.creative.id,
      file.meta,
    );
    lapi.signal.throwIfAborted();
    if (!bindResult.success) {
      return error('Failed to bind creative files');
    }
  }

  if (Object.keys(convertResult.metaDiff).length !== 0) {
    lapi.dispatch(
      creativeConverterActions.creativeFixed({
        creativeTypeID: settings.type.id,
        settings: convertResult.metaDiff,
      }),
    );
  }

  lapi.dispatch(creativeConverterActions.jobFinished({ nodeID: creativeConverterJob.node.id }));
  logger.lapi(lapi, {
    action: 'Convert',
    sub_action: 'Success',
    extra: { int_1: creativeConverterJob.node.id },
  });
  // перезагружаем ФМ
  lapi.dispatch(fileManagerActions.refreshRequested());
}

/**
 * @param nodeID folder ID
 * @param subfolderNameOptions possible subfolder names
 * @returns {SubfolderOptionToNodeMap[]}
 */
async function findAllFiles(lapi: ListenerApiLight, nodeID: number, settings: CreativeBLSettings) {
  return collectConvertSourceFiles({
    rootNodeId: nodeID,
    settings,
    getNodes: (parentNodeId: number) => getNodes(lapi, parentNodeId),
  });
}

async function prepareNodesFallbacks(
  lapi: ListenerApiLight,
  rootNode: CHNode,
  settings: CreativeBLSettings,
): Promise<NodeFallback[]> {
  await normalizeConvertRootNode({ rootNode, settings });
  lapi.signal.throwIfAborted();

  const items = await findAllFiles(lapi, rootNode.id, settings);
  lapi.signal.throwIfAborted();

  const cache = _select(lapi, getCache);
  const fallbacksAvailable = cache.creativeTypeID === settings.type.id;
  const useFallbacks = fallbacksAvailable
    ? cache.promptResult !== undefined
      ? cache.promptResult
      : await convertPopup(lapi)
    : false;
  lapi.signal.throwIfAborted();
  const fallback = useFallbacks ? cache.settings : {};

  return buildConvertNodeFallbacks(items, settings.type.id, fallback);
}

async function convertPopup(lapi: ListenerApiLight) {
  lapi.dispatch(creativeConverterActions.promptVisibilityChanged({ visible: true }));

  const action = await _take(lapi, creativeConverterActions.promptResolved);
  lapi.dispatch(creativeConverterActions.promptVisibilityChanged({ visible: false }));
  return action.payload.result;
}

function createErrorHandler(lapi: ListenerApiLight, nodeID: number) {
  return function returnWithError(error: string) {
    logger.lapi(lapi, { action: 'Convert', sub_action: 'Fail', extra: { int_1: nodeID } });
    lapi.dispatch(
      creativeConverterActions.jobErrorChanged({
        nodeID,
        error,
      }),
    );

    return false;
  };
}

function createBLOptions(settings: CreativeBLSettings, user: CurrentUser): BLOptions {
  // Бэкенд отдаёт оба поля `genre` (single, legacy) и `genres` (массив).
  // Если у креатива на сервере уже есть `genres` — используем его. Иначе
  // fallback на старое single-поле `genre`.
  const genres: number[] =
    settings.creative.genres && settings.creative.genres.length > 0
      ? settings.creative.genres
      : settings.creative.genre !== null && settings.creative.genre !== undefined
        ? [settings.creative.genre]
        : [];
  return {
    creativeName: settings.creative.node_name,
    creativeTypeId: settings.type.id,
    developerId: user.id,
    developer: user,
    managerId: user.id,
    manager: user,
    projectId: settings.project.id,
    mislead_level: settings.creative.mislead_level,
    teamID: user.team?.id ?? null,
    genres,
    intro: settings.creative.intro,
    ai_generated: settings.creative.ai_generated ?? false,
    concept_type: getConceptTypeId(settings.creative.concept_type),
    tagsActive: [],
    source_creatives: [],
    plot_recognition: getPlotRecognition(settings.creative.plot_recognition),
  };
}
